import React, { FC, useState, useEffect } from 'react';
import cx from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import useCommonStyles from 'styles/common';
import CenterImage from 'components/common/CenterImage';

import titleImg from 'assets/invoice/invoice-title.svg'; 
import subTitleImg from 'assets/invoice/sub-title.svg'; 
import recipesTitleImg from 'assets/invoice/recipes-title.svg'; 
import recipesTitleMobileImg from 'assets/invoice/recipes-title-mobile.svg'; 
import exampleCloudImg from 'assets/invoice/eg-cloud.png';
import examplePaperImg from 'assets/invoice/eg-paper.png';

import InlineTypography from 'components/common/InlineTypography';
import CommonButton from 'components/common/CommonButton';
import { Tomato01, Milk01, Cauli } from 'components/home/stamps';

import { ApiResponse, InvoiceForms, InvoiceFormValues } from 'types';

import FormInputs from './FormInputs';
import Guideline from './Guideline';
import CommonModal from 'components/common/CommonModal';
import Products from './Products';
import CustomAlert from 'components/common/CustomAlert';

const useStyles = makeStyles((theme) => ({
	invoiceSection: { 
		width: '100%',
		paddingBottom: 20,
		flexDirection: 'column',
		color: 'var(--color-tertiary)',
		position: 'relative',
        [theme.breakpoints.down('xs')]: {
			paddingBottom: 4
		}
	},
	invoiceSectionTitle: {
		marginTop: 36,
		width: 351
	},
	topText: {
        [theme.breakpoints.down('xs')]: {
			padding: '0 38px'
		}
	},
	subTitle: {
		marginTop: 36,
		width: 288,
        [theme.breakpoints.down('xs')]: {
			marginTop: 24,
			width: 240,
		}
	},
	formsContainer: {
		marginTop: 36,
		padding: '0 20px',
		alignItems: 'flex-start',
		maxWidth: 1440,
        [theme.breakpoints.down('xs')]: {
			flexDirection: 'column',
			gap: 36,
			padding: '0 15px'
		}
	},
	fromWrap: { // 包含標題和表單的部分
		flex: 1,
		width: '100%'
	},
	form: {
		marginTop: 24,
		borderRadius: 16,
		maxWidth: 700,
		padding: '40px 20px'
	},

	formLeft: { 
		backgroundColor: 'var(--color-primary)',
		marginRight: 4,
        [theme.breakpoints.down('xs')]: {
			marginRight: 0
		}
	},
	formLeftInputs: {
		marginRight: 16,
        [theme.breakpoints.down('xs')]: {
			margin: '0 10px 16px 10px'
		}
	}, 
	formRight: {
		backgroundColor: 'var(--color-secondary)',
		marginLeft: 4,
        [theme.breakpoints.down('xs')]: {
			marginLeft: 0
		}
	},
	formRightInputs: {
		marginLeft: 16,
        [theme.breakpoints.down('xs')]: {
			margin: '0 10px 16px 10px'
		}
	}, 

	formContent: {
		maxWidth: 532,
        [theme.breakpoints.down('xs')]: {
			width: '100%'
		}
	},

	formSubmitButton: {
		marginTop: 24,
        [theme.breakpoints.down('xs')]: {
			marginTop: 20
		}
	},

	imageUploader: {
		marginBottom: 20
	},

	exampleModelContent: {
		maxHeight: 'calc(80vh)',
		overflowY: 'auto',
		backgroundColor: theme.palette.background.paper,
    	borderRadius: 16,
		padding: '40px 50px',
        [theme.breakpoints.down('xs')]: {
			padding: '19px 37px',
			width: '70vw'
		}
	},

	cookbookEvent: {
		padding: '0 20px',
		position: 'relative'
	},

	recipesDesc: {
		marginTop: 24,
		color: 'var(--color-primary)',
        [theme.breakpoints.down('xs')]: {
			fontSize: 24,
			padding: '0 5px'
		}
	},

}));

interface InvoiceSectionProps {
	titleRef: React.RefObject<HTMLDivElement>;
	rulesRef: React.RefObject<HTMLDivElement>;
	submitInvoiceForm: (form: {}) => Promise<ApiResponse>
}

const InvoiceSection: FC<InvoiceSectionProps> = ({titleRef, rulesRef, submitInvoiceForm}) => {
	const classes = useStyles();
	const common = useCommonStyles();

	const [ isPaperExampleOpen, setPaperExampleOpen ] = useState(false);
	const [ isCloudExampleOpen, setCloudExampleOpen ] = useState(false);
	
	const [ invoiceFormValues, setInvoiceFormValues ] = useState<InvoiceForms>({
		paper: {},
		cloud: {}
	});

	const [ alertMessage, setAlertMessage] = useState('');
	const closeAlert = () => {
		setAlertMessage('');
	}

	const setInvoiceFormValueByKey = 
	<T extends keyof InvoiceForms, k extends keyof InvoiceFormValues>(type: T, key: k, value: any) => {	
		setInvoiceFormValues({
			...invoiceFormValues,
			[type]: {
				...invoiceFormValues[type],
				[key]: value
			}
		});
	}

	const sendInvoice = async <T extends keyof InvoiceForms>(type: T) => {
        const result = await submitInvoiceForm({
			type: type,
			...invoiceFormValues[type]
		});

        if (!result.isSent) { // ApiResponse 
			setAlertMessage(result.message || '欄位錯誤');
            return; // 表單內容檢查未通過
        } else if (result.isSuccess) {
			setAlertMessage('發票登錄完成，得獎將由主辦單位另行通知')
			// 只有這個做Modal
			setInvoiceFormValues({
				...invoiceFormValues,
				[type]: {}
			});
        } else {
			setAlertMessage('登錄失敗，請重新嘗試！');
        }
	}

	// TODO for dev
	useEffect(() => {
		console.log(invoiceFormValues)
	}, [invoiceFormValues])
	
	return (<div className={ cx(common.center, classes.invoiceSection) }>
		<CustomAlert open={!!alertMessage} message={ alertMessage } onClose={closeAlert} />
		
		<CenterImage divRef={titleRef} src={titleImg} alt='title' className={ classes.invoiceSectionTitle }/>
		<div className={classes.topText}>
			<Typography
				variant="body1"
				style={{ whiteSpace: 'pre-line', marginTop: 24 }}
			>
				{`6/6－7/3全聯實體門市、小時達、大潤發實體及線上通路購買好侍指定商品任兩盒
				(需含一盒蕃茄燴飯料理塊 或 北海道白醬系列料理塊)
				即可登錄發票參加抽獎！
				`}
			</Typography>
			<div className={ common.hideInMobile }>
				<InlineTypography variant="body1" >{`有機會獲得 `}</InlineTypography>
				<InlineTypography variant="subtitle1" color='var(--color-primary)'>{`日本九州熊本`}</InlineTypography>
				<InlineTypography variant="body1">{` 或 `}</InlineTypography>
				<InlineTypography variant="subtitle1" color='var(--color-primary)'>{`北海道來回機票`}</InlineTypography>
				<InlineTypography variant="body1">{` 及 `}</InlineTypography>
				<InlineTypography variant="subtitle1" color='var(--color-primary)'>{`人氣廚房家電！`}</InlineTypography>
			</div>

			<div className={ common.displayInMobile }>
				<InlineTypography variant="body1" >{`有機會獲得 `}</InlineTypography>
				<InlineTypography variant="subtitle1" color='var(--color-primary)'>{`日本九州熊本`}</InlineTypography>
				<InlineTypography variant="body1">{` 或 `}</InlineTypography>
			</div>
			<div className={ common.displayInMobile }>
				<InlineTypography variant="subtitle1" color='var(--color-primary)'>{`北海道來回機票`}</InlineTypography>
				<InlineTypography variant="body1">{` 及 `}</InlineTypography>
			</div>
			<div className={ common.displayInMobile }>
				<InlineTypography variant="subtitle1" color='var(--color-primary)'>{`人氣廚房家電！`}</InlineTypography>
			</div>

		</div>

		<Tomato01 style={{ top: 256, left: 'calc(50vw - 667px)' }} />
		<Milk01 style={{ top: 46, right: 'calc(50vw - 670px)' }} />
		<Tomato01 className={common.displayInMobile} style={{ top: 348, left: 12, width: 36 }} />
		<Milk01 className={common.displayInMobile} style={{ top: 380, right: 12, width: 26 }} />
		
		<CenterImage src={subTitleImg} alt='title' className={ classes.subTitle }/>
		<div className={ cx(common.center, common.fullWidth, classes.formsContainer) }>

			<div className={ classes.fromWrap }>
				<Typography variant='subtitle1' style={{ color: 'var(--color-primary)'}}>紙本發票</Typography>

				<div className={ cx(classes.form, classes.formLeft, common.columnCenter) }>
					<CommonButton
						buttonText="紙本發票範例"
						style={{ marginBottom: 26 }}
						color='var(--color-primary)'
						onClick={() => setPaperExampleOpen(true)}
					/>
					<div className={ cx(classes.formContent, common.columnCenter) }>
						<FormInputs 
							type='paper'
							invoiceFormValues={ invoiceFormValues }
							setInvoiceFormValueByKey={ setInvoiceFormValueByKey }
							imageUploaderColor={ 'var(--color-primary)' }
							inputColor={ 'var(--color-white)' }
							labelInputClassName={ classes.formLeftInputs }
						/>
					</div>
					<CommonButton
						buttonText="送出" className={ classes.formSubmitButton}
						onClick={() => sendInvoice('paper')}
					/>

					<CommonModal isOpen={isPaperExampleOpen} handleClose={() => setPaperExampleOpen(false)}>
						<div className={classes.exampleModelContent}> 
							<CenterImage src={examplePaperImg}/>
						</div>
					</CommonModal>
				</div>

			</div>

			<div className={ classes.fromWrap }>
				<Typography variant='subtitle1' style={{ color: 'var(--color-tertiary)'}}>雲端載具</Typography>
				
				<div className={ cx(classes.form, classes.formRight, common.columnCenter) }>

					<CommonButton
						buttonText="雲端載具範例"
						style={{ marginBottom: 26 }}
						color='var(--color-tertiary)'
						onClick={() => setCloudExampleOpen(true)}
					/>
					<div className={ cx(classes.formContent, common.columnCenter) }>
						<FormInputs 
							type='cloud'
							invoiceFormValues={ invoiceFormValues }
							setInvoiceFormValueByKey={ setInvoiceFormValueByKey }
							imageUploaderColor={ 'var(--color-tertiary)' }
							inputColor={ 'var(--color-tertiary)' }
							labelInputClassName={ classes.formRightInputs }
						/>
					</div>
					<CommonButton
						buttonText="送出" className={ classes.formSubmitButton}
						onClick={() => sendInvoice('cloud')}
					/>

					<CommonModal isOpen={isCloudExampleOpen} handleClose={() => setCloudExampleOpen(false)}>
						<div className={classes.exampleModelContent}> 
							<CenterImage src={exampleCloudImg}/>
						</div>
					</CommonModal>

				</div>

			</div>
		</div>

		<Guideline rulesRef={rulesRef}/>
					
		<Products />

		<div className={ cx(classes.cookbookEvent, common.columnCenter, common.fullWidth) }>
			<a 
				href={ "https://mwebapi.pxgo.com.tw/share/?shareNumber=9fac5e37c4e446c3a1c753664523d31e&channel=1" } 
				rel="noopener noreferrer" target="_blank"
			>
				<CenterImage 
					src={recipesTitleImg} alt='more-recipe' maxWidth={880}
					className={ cx(common.hideInMobile) } />
				<CenterImage 
					src={recipesTitleMobileImg} alt='more-recipe'maxWidth={328}
					className={ cx(common.displayInMobile) } />
			</a>
			
			<Typography variant='body1' style={{  }} className={classes.recipesDesc}>
				{`投稿食譜，最高10,000福利點、小時達3,000元料理金等你拿!`}
			</Typography>
			<Cauli style={{ top: -140, left: 'calc(50vw - 610px)',  width: 75}} />
			<Tomato01 style={{ top: -65, right: 'calc(50vw - 665px)', width: 95}} />
		</div>

	</div>);
};

export default InvoiceSection;
